'use client'

import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { FileText, Lock, Sparkles, TriangleAlert } from 'lucide-react'
import { DemoFrame } from './demoKit'
import { useOnScreen, useSequence } from './demoHooks'
import { ResultStamp, Trail } from './demoParts'

// Vibe coding: the page says it's "not just prompting and hoping". The same
// AI-built client portal, shipped two ways: straight to production (a client
// can see another client's files) or through a developer who catches it.

const PROMPT = 'Build a client portal with login and file sharing'

const PATHS = {
  hope: {
    trail: [
      { at: 1, text: 'Prompted the AI' },
      { at: 2, text: 'AI drafted the whole portal' },
      { at: 3, text: '"Looks fine", shipped without review', tone: 'bad' },
      { at: 4, text: "Client B can open Client A's files", tone: 'bad' },
    ],
    result: { tone: 'bad', text: 'Data leak in production' },
  },
  dev: {
    trail: [
      { at: 1, text: 'Prompted the AI' },
      { at: 2, text: 'AI drafted the whole portal' },
      { at: 3, text: 'Developer reviewed every screen and query' },
      { at: 4, text: 'Caught it: any client could see every file', tone: 'wait' },
      { at: 5, text: 'Added per-client access rules and tests' },
      { at: 6, text: 'Shipped live in 3 days' },
    ],
    result: { tone: 'good', text: 'Shipped safely' },
  },
}
const DELAYS = { hope: [500, 1200, 1200, 1200, 800], dev: [500, 1200, 1100, 1200, 1200, 1100, 700] }

const FILES = [
  { name: 'Contract.pdf', owner: 'You' },
  { name: 'Invoice-0424.pdf', owner: 'You' },
  { name: 'Payroll-Q3.xlsx', owner: 'Another client' },
]

function Portal({ step, path }) {
  const built = step >= 2
  const leak = path === 'hope' && step >= 4
  const flagged = path === 'dev' && step === 4
  const locked = path === 'dev' && step >= 5
  return (
    <div className={`svd-portal${leak ? ' is-leak' : ''}`}>
      <div className="svd-browser-bar" aria-hidden="true"><i /><i /><i /><span>portal.yourbrand.com</span></div>
      {built ? (
        <motion.div className="svd-portal-body" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="svd-portal-head">
            <strong>Client Portal</strong>
            <span className={`svd-chip${locked ? ' is-good' : ''}`}>{locked ? <><Lock size={11} aria-hidden="true" /> Only your files</> : 'Signed in as Client B'}</span>
          </div>
          <ul>
            {FILES.map((f, i) => {
              const hidden = locked && f.owner !== 'You'
              const exposed = (leak || flagged) && f.owner !== 'You'
              return (
                <motion.li
                  key={f.name}
                  className={`${exposed ? (leak ? 'is-leak' : 'is-flag') : ''}${hidden ? ' is-hidden' : ''}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: hidden ? 0.35 : 1, y: 0 }}
                  transition={{ delay: i * 0.12 }}
                >
                  <FileText size={14} aria-hidden="true" />
                  <span>{hidden ? 'Hidden: belongs to another client' : f.name}</span>
                  {exposed ? <TriangleAlert size={14} aria-hidden="true" /> : null}
                </motion.li>
              )
            })}
          </ul>
        </motion.div>
      ) : (
        <div className="svd-portal-empty"><Sparkles size={16} aria-hidden="true" /> AI is drafting the portal…</div>
      )}
    </div>
  )
}

export default function VibeBuild() {
  const [ref, onScreen] = useOnScreen()
  const [path, setPath] = useState('hope')
  const [run, setRun] = useState(0)
  const [auto, setAuto] = useState(true)
  const data = PATHS[path]
  const step = useSequence(DELAYS[path], { run: onScreen, resetKey: `${path}-${run}` })
  const finished = step >= DELAYS[path].length

  useEffect(() => {
    if (!auto || path !== 'hope' || !finished) return undefined
    const timer = setTimeout(() => setPath('dev'), 1800)
    return () => clearTimeout(timer)
  }, [auto, path, finished])

  const pick = (key) => { setAuto(false); setPath(key); setRun((n) => n + 1) }

  return (
    <DemoFrame
      frameRef={ref}
      className="svd-vibe"
      title="Same prompt, two outcomes"
      label="Demo: an AI-built client portal shipped without review versus reviewed by a GHL Prime developer"
      tabs={[{ key: 'hope', label: 'Prompt and hope', tone: 'bad' }, { key: 'dev', label: 'With a GHL Prime developer', tone: 'good' }]}
      activeTab={path}
      onTab={pick}
      onReplay={() => pick(path)}
    >
      <p className="svd-prompt"><Sparkles size={14} aria-hidden="true" /> {PROMPT}</p>
      <div className="svd-agent-grid">
        <Portal step={step} path={path} />
        <Trail title={path === 'dev' ? 'What the developer did' : 'What happened'} items={data.trail} step={step} />
      </div>
      <ResultStamp show={finished} tone={data.result.tone} text={data.result.text} id={`${path}-${run}`} />
    </DemoFrame>
  )
}
